import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { SECTOR_LABELS } from "../../data/mockData";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useFunderProgramsHook } from "../../hooks/useFunderHooks";
import {
  Card,
  PageHeader,
  CHART_COLORS,
} from "../fundercomponents/funderComponents";
import "./programs.css";

const TABS = [
  { id: "geography", label: "By Geography" },
  { id: "cohort", label: "By Funding Period" },
  { id: "sector", label: "By Sector" },
];

export default function Programs() {
  const { user, token } = useAuth();
  const isFunder = user?.role === ROLES.FUNDER;
  const [activeTab, setActiveTab] = useState("geography");

  const { data: programsApiData, isLoading } = useFunderProgramsHook({
    token,
    userId: user?.id,
    enabled: isFunder,
  });

  const programsData = programsApiData ?? {
    program_views: {
      by_geography: {
        impact: [],
        regional_breakdown: [],
      },
      by_funding_period: {
        impact: [],
        cohorts: [],
      },
      by_sector: {
        impact: [],
        sector_performance: [],
      },
    },
  };

  const geographyImpact = programsData.program_views.by_geography.impact.map(
    (row) => ({
      region: row.region ?? "-",
      ventures: Number(row.ventures ?? 0),
      jobs: Number(row.jobs ?? 0),
    }),
  );
  const geographyRows =
    programsData.program_views.by_geography.regional_breakdown;

  const cohortImpact = programsData.program_views.by_funding_period.impact.map(
    (row) => ({
      cohort: row.cohort ?? "-",
      jobs: Number(row.jobs ?? 0),
      beneficiaries: Number(row.beneficiaries ?? 0),
    }),
  );
  const cohortRows = programsData.program_views.by_funding_period.cohorts;

  const sectorImpact = programsData.program_views.by_sector.impact.map(
    (row) => ({
      sector:
        SECTOR_LABELS[String(row.sector ?? "").toLowerCase()] ??
        row.sector ??
        "-",
      ventures: Number(row.ventures ?? 0),
      jobs: Number(row.jobs ?? 0),
    }),
  );
  const sectorRows = programsData.program_views.by_sector.sector_performance;

  const summaryCards = [
    {
      label: "Regions Covered",
      value: geographyRows.length,
      detail: `${sumValues(geographyRows, "ventures")} ventures across geography`,
    },
    {
      label: "Jobs Created",
      value: sumValues(geographyRows, "jobs").toLocaleString(),
      detail: "Across the regional program view",
    },
    {
      label: "End Customers Reached",
      value: sumValues(geographyRows, "beneficiaries").toLocaleString(),
      detail: "Regional program reporting",
    },
  ];

  const views = {
    geography: {
      title: "Program Views by Geography",
      description: "Regional reach, job creation, and capital deployment.",
      chartData: geographyImpact,
      chartXAxis: "region",
      chartBars: [
        { key: "ventures", name: "Ventures", fill: CHART_COLORS[0] },
        { key: "jobs", name: "Jobs", fill: CHART_COLORS[1] },
      ],
      chartVertical: false,
      rows: geographyRows,
      rowKey: "region",
      columns: [
        { key: "region", label: "Region" },
        { key: "ventures", label: "Ventures", align: "right" },
        { key: "jobs", label: "Jobs", align: "right" },
        {
          key: "beneficiaries",
          label: "End Customers",
          align: "right",
          format: formatNumber,
        },
        { key: "capital", label: "Capital", align: "right" },
      ],
    },
    cohort: {
      title: "Program Views by Funding Period",
      description: "Cohort-level jobs, end customers, and revenue.",
      chartData: cohortImpact,
      chartXAxis: "cohort",
      chartBars: [
        { key: "jobs", name: "Jobs", fill: CHART_COLORS[0] },
        {
          key: "beneficiaries",
          name: "End Customers",
          fill: CHART_COLORS[2],
        },
      ],
      chartVertical: false,
      rows: cohortRows,
      rowKey: "cohort",
      columns: [
        { key: "cohort", label: "Cohort" },
        { key: "ventures", label: "Ventures", align: "right" },
        { key: "jobs", label: "Jobs", align: "right" },
        {
          key: "beneficiaries",
          label: "End Customers",
          align: "right",
          format: formatNumber,
        },
        { key: "revenue", label: "Revenue", align: "right" },
      ],
    },
    sector: {
      title: "Program Views by Sector",
      description: "Sector performance and portfolio concentration.",
      chartData: sectorImpact,
      chartXAxis: "sector",
      chartBars: [{ key: "jobs", name: "Jobs", fill: CHART_COLORS[0] }],
      chartVertical: true,
      rows: sectorRows,
      rowKey: "sector",
      columns: [
        { key: "sector", label: "Sector" },
        { key: "ventures", label: "Ventures", align: "right" },
        { key: "jobs", label: "Jobs", align: "right" },
        {
          key: "beneficiaries",
          label: "End Customers",
          align: "right",
          format: formatNumber,
        },
        { key: "revenue", label: "Revenue", align: "right" },
      ],
    },
  };

  const currentView = views[activeTab];

  if (isLoading && isFunder) {
    return (
      <div className="funderPrograms">
        <PageHeader
          title="Program Views"
          description="Impact analysis by cohort, geography, and sector."
        />
        <p className="admin-muted">Loading program views...</p>
      </div>
    );
  }

  return (
    <div className="funderPrograms">
      <PageHeader
        title="Program Views"
        description="Impact analysis by cohort, geography, and sector."
      />

      <div className="funderPrograms-stats">
        {summaryCards.map((card) => (
          <article key={card.label} className="funderPrograms-statCard">
            <p className="funderPrograms-statValue">{card.value}</p>
            <p className="funderPrograms-statLabel">{card.label}</p>
            <p className="funderPrograms-statDetail">{card.detail}</p>
          </article>
        ))}
      </div>

      <div className="funderPrograms-tabs" role="tablist">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={
                "funderPrograms-tab" +
                (isActive ? " funderPrograms-tab-active" : "")
              }
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="funderPrograms-section">
        <Card title={currentView.title} description={currentView.description}>
          <div className="funderPrograms-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={currentView.chartData}
                layout={currentView.chartVertical ? "vertical" : "horizontal"}
              >
                <CartesianGrid strokeDasharray="3 3" />
                {currentView.chartVertical ? (
                  <>
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey={currentView.chartXAxis}
                      width={120}
                    />
                  </>
                ) : (
                  <>
                    <XAxis dataKey={currentView.chartXAxis} />
                    <YAxis allowDecimals={false} />
                  </>
                )}
                <Tooltip />
                <Legend />
                {currentView.chartBars.map((bar) => (
                  <Bar
                    key={bar.key}
                    dataKey={bar.key}
                    name={bar.name}
                    fill={bar.fill}
                    radius={4}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title={`${currentView.title} Details`}>
          <DataTable
            columns={currentView.columns}
            rows={currentView.rows}
            rowKey={currentView.rowKey}
          />
        </Card>
      </div>
    </div>
  );
}

function sumValues(rows, key) {
  return rows.reduce((sum, row) => sum + Number(row?.[key] ?? 0), 0);
}

function formatNumber(value) {
  return Number(value ?? 0).toLocaleString();
}

function DataTable({ columns, rows, rowKey }) {
  return (
    <div className="funderPrograms-tableWrap">
      <table className="funderPrograms-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={
                  col.align === "right" ? { textAlign: "right" } : undefined
                }
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[rowKey]}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={
                    col.align === "right" ? { textAlign: "right" } : undefined
                  }
                >
                  {col.format ? col.format(row[col.key]) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
